import { EntityAdapter, EntityState } from '@ngrx/entity';

import { createSelector, MemoizedSelector } from '@ngrx/store';
import { Pagination } from '../models/pagination';

export interface PaginationState<T = object> {
  itemsList: EntityState<T>;
  pagination: Pagination<T>;
  areItemsLoaded: boolean;
  shouldLoadElements: boolean;
  lastRemoved?: { item: T; index: number };
}

export function getPaginationInitialState<T>(adapter: EntityAdapter<T>): PaginationState<T> {
  return {
    itemsList: adapter.getInitialState(),
    pagination: {
      last: true,
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: 0,
      numberOfElements: 0,
      loading: false,
    },
    areItemsLoaded: false,
    shouldLoadElements: false,
  } as PaginationState<T>;
}

export function handleLoading<T>(state: T): T {
  return {
    ...state,
    pagination: {
      ...(state as PaginationState<unknown>).pagination,
      loading: true,
    },
    areItemsLoaded: false,
  };
}

export function handleLoadingItemsSuccess<T extends PaginationState<U>, U>(
  state: T,
  payload: Pagination<U>,
  adapter: EntityAdapter<U>,
): T {
  return {
    ...state,
    itemsList: adapter.upsertMany(payload.content, state.itemsList),
    pagination: {
      last: payload?.last,
      totalElements: payload?.totalElements,
      totalPages: payload?.totalPages,
      number: payload?.number,
      size: payload?.size,
      numberOfElements: payload?.numberOfElements,
      loading: payload?.loading ?? false,
    },
    areItemsLoaded: true,
  };
}

export function handleLoadingItemsSuccessWithSetAll<T extends PaginationState<U>, U>(
  state: T,
  payload: Pagination<U>,
  adapter: EntityAdapter<U>,
): T {
  return {
    ...state,
    itemsList: adapter.setAll(payload.content, state.itemsList),
    pagination: {
      last: payload?.last,
      totalElements: payload?.totalElements,
      totalPages: payload?.totalPages,
      number: payload?.number,
      size: payload?.size,
      numberOfElements: payload?.numberOfElements,
      loading: payload?.loading ?? false,
    },
    areItemsLoaded: true,
  };
}

export function handleRemoveOneAction<T extends PaginationState<U>, U>(
  state: T,
  id: string,
  adapter: EntityAdapter<U>,
): T {
  const index = state.itemsList.ids.findIndex((item) => item === id);
  const item = state.itemsList.entities[id];
  const updatedTotalElements = state.pagination.totalElements - 1;
  const updatedItemsList = adapter.removeOne(id, state.itemsList);

  return {
    ...state,
    itemsList: updatedItemsList,
    pagination: {
      ...state.pagination,
      totalElements: updatedTotalElements,
    },
    lastRemoved: { item, index },
    shouldLoadElements: updatedItemsList.ids.length === 0 && updatedTotalElements !== 0,
  };
}

export function handleRemoveManyAction<T extends PaginationState<U>, U>(
  state: T,
  ids: string[],
  adapter: EntityAdapter<U>,
): T {
  const updatedTotalElements = state.pagination.totalElements - ids.length;
  return {
    ...state,
    itemsList: adapter.removeMany(ids, state.itemsList),
    pagination: {
      ...state.pagination,
      totalElements: updatedTotalElements,
    },
    shouldLoadElements: updatedTotalElements % state.pagination.size === 0,
  };
}

export function handleAddOneAction<T extends PaginationState<U>, U>(
  state: T,
  payload: U,
  adapter: EntityAdapter<U>,
) {
  return {
    ...state,
    itemsList: adapter.addOne(payload, state.itemsList),
    pagination: {
      ...state.pagination,
      totalElements: state.pagination.totalElements + 1,
    },
  };
}

export function handleRestoreOneAction<T extends PaginationState<U>, U>(
  state: T,
  payload: U,
  adapter: EntityAdapter<U>,
) {
  if (!state.lastRemoved) return state;
  const { index } = state.lastRemoved;
  const newItemsList: U[] = [...Object.values(state.itemsList.entities)] as U[];

  newItemsList.splice(index, 0, payload);

  return {
    ...state,
    itemsList: adapter.setAll(newItemsList, state.itemsList),
    pagination: {
      ...state.pagination,
      totalElements: state.pagination.totalElements + 1,
    },
    lastRemoved: undefined,
  };
}

export function handleUpsertOneAction<T extends PaginationState<U>, U>(
  state: T,
  payload: U,
  adapter: EntityAdapter<U>,
) {
  const updatedItemsList = adapter.upsertOne(payload, state.itemsList);
  const addedCount = updatedItemsList.ids.length - state.pagination.totalElements;
  return {
    ...state,
    itemsList: updatedItemsList,
    pagination: {
      ...state.pagination,
      totalElements: state.pagination.totalElements + addedCount,
    },
  };
}

export function handleUpdateOneAction<T extends PaginationState<U>, U>(
  state: T,
  payload: U,
  id: string,
  adapter: EntityAdapter<U>,
): T {
  return {
    ...state,
    itemsList: adapter.updateOne({ id, changes: payload }, state.itemsList),
  };
}

export function resetPaginationState<T extends PaginationState<U>, U>(
  state: T,
  adapter: EntityAdapter<U>,
): T {
  return {
    ...state,
    itemsList: adapter.removeAll(state.itemsList),
    pagination: {
      last: true,
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: 0,
      loading: false,
    },
    areItemsLoaded: false,
  };
}

export function getPaginationSelectors<RootState, T>(
  rootSelector: MemoizedSelector<RootState, RootState>,
  selectPaginationState: (state: RootState) => PaginationState<T> = (s: unknown) =>
    s as PaginationState<T>,
) {
  const paginationSelector = createSelector(rootSelector, selectPaginationState);

  return {
    getEntities: createSelector(paginationSelector, (state) => state.itemsList),
    isLoaded: createSelector(paginationSelector, (state) => state.areItemsLoaded),
    paginationData: createSelector(paginationSelector, (state) => state.pagination),
    shouldLoadElements: createSelector(paginationSelector, (state) => state.shouldLoadElements),
    lastRemoved: createSelector(paginationSelector, (state) => state.lastRemoved),
  };
}
